import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ReasonRefund } from '../models/enums/reason-refund.enum';
import { HandlingTypeEnum } from '../models/enums/handling-type.enum';
import { COUNTRIES } from '../models/constants/countries.const';
import { TranslateService } from '@ngx-translate/core';
import { inject } from '@angular/core';

export class BaryValidator {
  static translateService: TranslateService;

  constructor() {
    // Le service de traduction est instancié (injecté) dans le constructeur, car il a besoin d'un contexte d'injection pour être instancié (injecté).
    BaryValidator.translateService = inject(TranslateService);
  }

  static requireTrueOrError(messageKey: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return { customMessage: messageKey };
      }
      return null;
    };
  }

  static checkAtLeastOneForGroup(otherControls: FormControl[][], key: string, message: string, targetFormGroup: FormGroup): ValidatorFn {
    return (): ValidationErrors | null => {
      const allGroupHaveAtLeastOneValue = otherControls.every(group => group.filter(it => it.value !== null && it.value !== undefined && it.value !== '' && it.valid).length > 0);
      if (allGroupHaveAtLeastOneValue) {
        return null;
      } else {
        const error = BaryValidator.createError(key, message);
        targetFormGroup.setErrors(error);
        return error;
      }
    };
  }

  static timeslotRangeValidator(pickupTimeslotStart: AbstractControl, pickupTimeslotEnd: AbstractControl, pickupTimeslotRange: AbstractControl, errorKey: string): ValidatorFn {
    return (): ValidationErrors | null => {
      const start = pickupTimeslotStart?.value;
      const end = pickupTimeslotEnd?.value;
      const range = pickupTimeslotRange?.value;

      if (start == null || end == null || range == null) {
        return null; // Do nothing if fields are empty
      }
      if (end - start < range) {
        return { [errorKey]: true }; // Return an error if the condition is met
      }

      return null; // Valid
    };
  }

  static hasMinValueLowerThanMaxValue(minControl: FormControl, maxControl: FormControl, offset = 0, offsetUnit = ''): ValidatorFn {
    return () => {
      const minOrMax = minControl.value ?? maxControl.value; // Retourne la valeur de min si elle n'est pas null ou undefined ou la valeur de max (peu importe sa valeur)
      const isMinOrMaxMissing = typeof minOrMax === 'undefined' || minOrMax === null; // Vérifie si la valeur précédente est bien définie

      if (!isMinOrMaxMissing) {
        const minValue = parseFloat(minControl.value || 0);
        const maxValue = parseFloat(maxControl.value || 0);

        if (minValue > maxValue) {
          minControl.setErrors({ invalidMinMaxValue: true });
          // On utilise le ? sur le service, car il peut être undefined lors du chargement du composant
          return this.createError('invalid_min_max_value', this.translateService?.instant('ERROR.MIN_MAX_VALUE'));
        }

        if (Math.abs(maxValue - minValue) < offset) {
          minControl.setErrors({ invalidValueForOffset: { offset, offsetUnit } });
          maxControl.setErrors({ invalidValueForOffset: { offset, offsetUnit } });
          return this.createError('invalid_value_for_offset', this.translateService?.instant('ERROR.LOWER_THAN_OFFSET', { offset, offsetUnit }));
        }
      }

      // If the error is not present, remove it
      delete minControl.errors?.['invalidMinMaxValue'];
      delete minControl.errors?.['invalidValueForOffset'];
      delete maxControl.errors?.['invalidValueForOffset'];

      // If there is no more error, set the object to null to validate the input
      if (Object.keys(minControl.errors || {}).length === 0) {
        minControl.setErrors(null);
      }
      if (Object.keys(maxControl.errors || {}).length === 0) {
        maxControl.setErrors(null);
      }

      return null;
    };
  }

  static checkAtLeastOneForGroupWithActiveControl(
    activeControl: FormControl,
    otherControls: FormControl[][],
    key: string,
    message: string,
    targetFormGroup: FormGroup
  ): ValidatorFn {
    return (): ValidationErrors | null => {
      if (activeControl.value) {
        const allGroupHaveAtLeastOneValue = otherControls.every(group => group.filter(it => it.value !== null && it.value !== undefined && it.value !== '' && it.valid).length > 0);
        if (allGroupHaveAtLeastOneValue) {
          return null;
        } else {
          const error = BaryValidator.createError(key, message);
          targetFormGroup.setErrors(error);
          return error;
        }
      } else {
        targetFormGroup.setErrors(BaryValidator.cleanSpecificError(targetFormGroup.errors, key));
        return null;
      }
    };
  }

  static maxForPalletAndParcel(handlingTypeControl: FormControl, maxPallet?: number, maxParcel?: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (handlingTypeControl.value === HandlingTypeEnum.PALLET) {
        if (maxPallet && control.value > maxPallet) {
          return { max: { max: maxPallet, actual: control.value } };
        }
      } else {
        if (maxParcel && control.value > maxParcel) {
          return { max: { max: maxParcel, actual: control.value } };
        }
      }
      return null;
    };
  }

  static minForPalletAndParcel(handlingTypeControl: FormControl): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (handlingTypeControl.value === HandlingTypeEnum.PALLET) {
        if (control.value < 10) {
          return { min: { min: 10, actual: control.value } };
        }
      } else {
        if (control.value < 1) {
          return { min: { min: 1, actual: control.value } };
        }
      }
      return null;
    };
  }

  static createError(key: string, message: string): ValidatorFn {
    const object = Object.create({});
    object[key] = message;
    return object as ValidatorFn;
  }

  private static cleanSpecificError(errors: ValidationErrors | null, key: string): any {
    if (errors) {
      const newError = Object.create({});
      Object.keys(errors).forEach(_key => {
        if (_key !== key) {
          newError[_key] = errors[_key];
        }
      });
      if (Object.keys(newError).length === 0) {
        return null;
      } else {
        return newError;
      }
    } else {
      return null;
    }
  }

  static multiEmail(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const emailPattern =
        /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      const containInvalidEmail = control.value?.split(';').some((element: string) => !emailPattern.test(element));
      return containInvalidEmail ? this.createError('containInvalidEmail', 'Une des adresses mail est invalide ou non séparée par un « ; »') : null;
    };
  }

  static validatorRefundRequired(): ValidatorFn {
    return (formGroup: any): ValidationErrors | null => {
      if (
        (formGroup.controls.reason.value === ReasonRefund.EMPTY_REASON && !formGroup.controls.refundAmount.value && !formGroup.controls.contractor?.value) ||
        (formGroup.controls.reason.value !== ReasonRefund.EMPTY_REASON && formGroup.controls.refundAmount.value > 0 && formGroup.controls.contractor?.value)
      ) {
        return null;
      }
      return { refundRequired: true };
    };
  }

  static validateZipCodeAccordingCountryCode(zipCodeControl: AbstractControl, countryCodeControl: AbstractControl): ValidatorFn {
    return (): ValidationErrors | null => {
      if (!countryCodeControl || !countryCodeControl.value || !zipCodeControl.value) {
        return null;
      }

      const countryCode = (countryCodeControl.value as string).slice(0, 2).toUpperCase();
      const zipCode = (zipCodeControl.value as string).toUpperCase();

      if (COUNTRIES[countryCode].zipCodePattern) {
        COUNTRIES[countryCode].zipCodePattern!.test(zipCode)
          ? zipCodeControl.setErrors(this.cleanSpecificError(zipCodeControl.errors, 'invalidZipCode'))
          : zipCodeControl.setErrors({ ...zipCodeControl.errors, invalidZipCode: { value: countryCode } });
      }

      return null;
    };
  }
}
